import type { NewsRelevance } from "@shared/newsRelevance";
import { NEWS_REASON_LABELS, relevanceSummary } from "@shared/newsRelevance";
import type { NewsEvidence } from "@shared/newsArticleIdentity";
import InformationDisplay from "@/components/InformationDisplay";
import React from "react";

function statusLabel(relevance: NewsRelevance): string {
  switch (relevance.status) {
    case "potentially-material": return "Potentially market-relevant";
    case "economic-context": return "Financial or economic context";
    case "uncertain": return "Relevance uncertain";
    case "irrelevant": return "No identified market relevance";
  }
}

function renderAssessment(relevance: NewsRelevance) {
  const reasons = relevance.reasons.slice(0, 3).map((reason) => NEWS_REASON_LABELS[reason.code]).join("; ");
  return (
    <span>
      {statusLabel(relevance)}
      {reasons ? ` · ${reasons}` : ` · ${relevanceSummary(relevance)}`}
      {" · Market reaction not established."}
    </span>
  );
}

/** Show the text-based relevance assessment separately from observed reaction. */
export default function NewsRelevanceDisplay({
  relevance,
  newsEvidence,
  compact = false,
}: {
  relevance?: NewsRelevance | null;
  newsEvidence?: NewsEvidence[];
  compact?: boolean;
}) {
  const evidence = newsEvidence ?? [];
  const primary = relevance ?? evidence[0]?.relevance;
  if (!primary && evidence.length === 0) return null;

  return (
    compact ? (
      <span className="news-relevance-display" aria-label="News relevance evidence" style={{ display: "block", fontSize: 11, fontWeight: 400 }}>
        {primary && renderAssessment(primary)}
      </span>
    ) : (
    <div className="news-relevance-display" aria-label="News relevance evidence">
      {primary && evidence.length === 0 && <small>{renderAssessment(primary)}</small>}
      {evidence.length > 0 && (
        <ul>
          {evidence.slice(0, 3).map((item, index) => (
            <li key={item.id ?? `${item.publisher}-${index}`}>
              <span>{item.publisher}: {item.title}</span>
              <small>{renderAssessment(item.relevance)}</small>
              <InformationDisplay information={item.information} expandable={false} />
            </li>
          ))}
          {evidence.length > 3 && <li><small>{evidence.length - 3} more source item(s)</small></li>}
        </ul>
      )}
    </div>
    )
  );
}
